/**
 * Color utility functions for theme generation
 * Supports HSL manipulation for dynamic theme variants
 */

export interface HSLValues {
  h: number; // 0-360
  s: number; // 0-100
  l: number; // 0-100
}

export interface PrimaryVariants {
  primary: string;
  primaryForeground: string;
  primaryHover: string;
  primaryActive: string;
  primaryMuted: string;
}

/**
 * Convert hex color to HSL values object
 */
export function hexToHSLValues(hex: string): HSLValues {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Convert HSL values to hex color
 */
export function hslToHex(hsl: HSLValues): string {
  const h = hsl.h / 360;
  const s = hsl.s / 100;
  const l = hsl.l / 100;

  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (x: number) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, '0');

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Calculate relative luminance for WCAG contrast
 */
function getLuminance(hex: string): number {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * Get contrasting text color (black or white) for a background
 * Uses WCAG luminance calculation
 */
export function getContrastColor(backgroundColor: string): string {
  const luminance = getLuminance(backgroundColor);
  // Pure black/white for WCAG contrast ratio calculation - intentionally not theme tokens
  return luminance > 0.179 ? '#000000' : '#FFFFFF';
}

/**
 * Adjust lightness of a hex color
 * @param hex - Hex color
 * @param amount - Amount to adjust (-100 to 100)
 */
export function adjustLightness(hex: string, amount: number): string {
  const hsl = hexToHSLValues(hex);
  hsl.l = Math.max(0, Math.min(100, hsl.l + amount));
  return hslToHex(hsl);
}

/**
 * Adjust saturation of a hex color
 * @param hex - Hex color
 * @param amount - Amount to adjust (-100 to 100)
 */
export function adjustSaturation(hex: string, amount: number): string {
  const hsl = hexToHSLValues(hex);
  hsl.s = Math.max(0, Math.min(100, hsl.s + amount));
  return hslToHex(hsl);
}

/**
 * Generate primary color variants based on theme mode
 * @param primaryHex - Primary brand color in hex
 * @param isDark - Whether dark mode is active
 */
export function generatePrimaryVariants(primaryHex: string, isDark: boolean): PrimaryVariants {
  const hsl = hexToHSLValues(primaryHex);

  // Foreground: auto-calculate for contrast
  const primaryForeground = getContrastColor(primaryHex);

  // Hover: lighter in dark mode, darker in light mode
  const hoverAmount = isDark ? 10 : -10;
  const primaryHover = adjustLightness(primaryHex, hoverAmount);

  // Active: more pronounced than hover
  const activeAmount = isDark ? 15 : -15;
  const primaryActive = adjustLightness(primaryHex, activeAmount);

  // Muted: desaturated and adjusted for mode
  const mutedHsl: HSLValues = {
    h: hsl.h,
    s: Math.round(hsl.s * 0.3), // 30% saturation
    l: isDark ? 15 : 95, // Dark bg in dark mode, light bg in light mode
  };
  const primaryMuted = hslToHex(mutedHsl);

  return {
    primary: primaryHex,
    primaryForeground,
    primaryHover,
    primaryActive,
    primaryMuted,
  };
}

/**
 * Convert hex to HSL string for CSS variable (without hsl() wrapper)
 * Format: "H S% L%"
 */
export function hexToHSLString(hex: string): string {
  const hsl = hexToHSLValues(hex);
  return `${hsl.h} ${hsl.s}% ${hsl.l}%`;
}
